import React, { useState } from 'react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { RETIREMENT_METHODS, ASSET_ICONS, ASSET_LABELS } from '../utils';

// Retiring is a soft delete: the API stamps deleted_at, sets status to
// 'retired', records retired_at and the disposal method, and writes an audit
// log entry. The row survives, but every list filters on deleted_at IS NULL, so
// the asset disappears from the app. IT admin only, enforced server-side.
export default function RetireAssetModal({ asset, onClose, onSuccess }) {
  const { withAuth } = useAuth();
  const [retirementMethod, setRetirementMethod] = useState('e-waste');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function handleRetire() {
    setSubmitting(true);
    setError('');
    try {
      await withAuth(() => api.retireAsset(asset.id, { retirementMethod }));
      onSuccess();
    } catch (err) {
      setError(err.message || 'Failed to retire asset');
    } finally {
      setSubmitting(false);
    }
  }

  // No click-outside-to-close: a stray backdrop click would discard typed work.
  return (
    <div className="modal-overlay">
      <div className="modal">
        <h3>Retire Asset</h3>

        <div style={{
          background: 'var(--bg-2)', borderRadius: 'var(--radius-sm)',
          padding: '12px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 22 }}>{ASSET_ICONS[asset.asset_type]}</span>
          <div>
            <div style={{ fontWeight: 500, fontSize: 14 }}>{asset.display_name}</div>
            <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
              {asset.asset_tag} · {ASSET_LABELS[asset.asset_type]}
            </div>
          </div>
        </div>

        <div style={{
          background: 'var(--red-dim)', border: '1px solid var(--red)',
          borderRadius: 'var(--radius-sm)', padding: '10px 14px',
          fontSize: 13, color: 'var(--text-1)', marginBottom: 16,
        }}>
          ⚠ This removes the asset from Assets, reports, and the map. Its history
          is kept and the action is audit-logged, but reversing it requires
          database access — there's no un-retire in the app.
          {asset.employee_name && (
            <><br /><br />It is currently assigned to <strong>{asset.employee_name}</strong>.</>
          )}
        </div>

        <div className="form-group">
          <label>Disposal Method</label>
          <select value={retirementMethod} onChange={e => setRetirementMethod(e.target.value)}>
            {RETIREMENT_METHODS.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>

        {error && <div style={{ color: 'var(--red)', fontSize: 14, marginBottom: 12 }}>{error}</div>}

        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" onClick={handleRetire} disabled={submitting}>
            {submitting ? 'Retiring…' : 'Retire Asset'}
          </button>
        </div>
      </div>
    </div>
  );
}
